'use client'

import { useQuery } from '@tanstack/react-query'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { useTokenActions } from '@/src/hooks/token-info/use-token-actions'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import { useGlobalSearch } from '@/src/queries/hooks/tokens'
import { useSearchHistoryAssetData } from '@/src/queries/hooks/use-search-history-asset-data'
import { trendingTokensQuery } from '@/src/queries/keys/tokens'
import type { TrendingAsset } from '@/src/services/tokens'
import { getCurrentPageType } from '@/src/utils/url'
import { Empty } from './empty'
import { Item } from './item'
import { List } from './list'
// import { LoadingOverlay } from './loading-overlay'
import { RecentAndTrending } from './recent-and-trending'
import { SearchCommand } from './search-command'
import { SearchInput } from './search-input'
import { type SearchResult, tokenId } from './types'

type GlobalSearchProps = {
  tabIndex?: number
  close: () => void
}

export function GlobalSearch({ tabIndex, close }: GlobalSearchProps) {
  const pathname = usePathname()
  const { ampli } = useAmplitudeAnalytics()

  const [isOpen, setIsOpen] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState('')
  const [assetQuery] = useDebounce(inputValue, 200)
  const [selectedValue, setSelectedValue] = useState<string>('')

  const [searchHistory, addToSearchHistory] = useSearchHistoryAssetData()

  const { data: searchResults = [], isFetched } = useGlobalSearch({
    assetQuery,
    enabled: !!inputValue && !!assetQuery,
  })

  const {
    pushRoute: pushTokenInfoRoute,
    prefetchRoute: prefetchTokenInfoRoute,
  } = useTokenActions()

  const { data: trending } = useQuery({
    ...trendingTokensQuery(),
    select: (assets: TrendingAsset[]): SearchResult[] =>
      assets.map((asset) => ({
        icon: asset.logo_img_url,
        name: asset.display_name,
        symbol: asset.ticker,
        denom: asset.denom,
        price: asset.price_usd,
        change: asset.price_change_percentage_24h,
        chainId: asset.chain_id,
      })),
  })

  useEffect(() => {
    if (searchResults.length > 0 && isFetched) {
      const firstResult = searchResults[0]
      setSelectedValue(`search-${tokenId(firstResult)}`)
    }
  }, [searchResults, isFetched])

  useEffect(() => {
    const isCommandOrCtrlK = (e: KeyboardEvent) =>
      e.key === 'k' && (e.metaKey || e.ctrlKey)
    const isForwardSlash = (e: KeyboardEvent) => e.key === '/'
    const triggers = [isCommandOrCtrlK, isForwardSlash]

    const down = (e: KeyboardEvent) => {
      if (triggers.some((check) => check(e))) {
        e.preventDefault()
        setIsOpen((open) => !open)
        isOpen ? inputRef.current?.blur() : inputRef.current?.focus()
      }
      if (isOpen && e.key === 'Escape') {
        e.preventDefault()
        setIsOpen(false)
        inputRef.current?.blur()
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [isOpen])

  const handleClose = () => {
    setIsOpen(false)
    inputRef.current?.blur()
    close()
  }

  const handleSelect = (item: SearchResult) => {
    addToSearchHistory(item)
    setInputValue('')

    pushTokenInfoRoute({
      chain_id: item.chainId,
      denom: item.denom,
      ticker: item.symbol,
      direction: 'to',
    })
    handleClose()

    ampli?.pageInteraction({
      page: getCurrentPageType(),
      key: 'click on search result item',
      context: item.symbol,
    })
  }

  const handleHover = useCallback(
    (item: SearchResult) => {
      prefetchTokenInfoRoute({
        chain_id: item.chainId,
        denom: item.denom,
        ticker: item.symbol,
        direction: 'to',
      })
    },
    [prefetchTokenInfoRoute],
  )

  if (pathname.startsWith('/route/')) {
    return null
  }

  return (
    <div className='relative flex flex-col items-start justify-start grow sm:grow-0 h-full sm:h-fit'>
      <SearchCommand
        shouldFilter={false}
        loop
        isOpen={isOpen}
        value={selectedValue}
        onValueChange={setSelectedValue}
        className='flex flex-col items-start w-full grow h-full sm:grow-0 sm:max-h-[36rem]'
      >
        <SearchInput
          className='flex items-center justify-between cursor-pointer border-b-1 border-b-border1 pl-4 pr-22 sm:px-5 sm:py-2 -mb-px z-[1]'
          isOpen={isOpen}
          isFetched={isFetched}
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsOpen(true)
            inputRef.current?.focus()
          }}
          onReset={() => {
            setInputValue('')
            inputRef.current?.focus()
          }}
          inputRef={inputRef}
          inputProps={{
            value: inputValue,
            onValueChange: setInputValue,
            // onBlur: handleClose,
            tabIndex: tabIndex,
            onFocus: () => setIsOpen(true),
          }}
        />
        {
          /* {isOpen && */
          !inputValue || !isFetched ? (
            <List
              disableScroll={Boolean(inputValue) && !isFetched}
              className={inputValue && 'animate-pulse opacity-40'}
            >
              <RecentAndTrending
                handleSelect={handleSelect}
                handleHover={handleHover}
                recentSearches={searchHistory}
                trending={trending ?? []}
              />
            </List>
          ) : (
            <>
              {searchResults.length === 0 && isFetched && (
                <List>
                  <Empty searchTerm={assetQuery} />
                </List>
              )}
              <List>
                {searchResults.map((asset) => (
                  <Item
                    key={`search-${tokenId(asset)}`}
                    item={asset}
                    onSelect={handleSelect}
                    onHover={handleHover}
                    prefix='search'
                  />
                ))}
              </List>
            </>
          )
        }
      </SearchCommand>
    </div>
  )
}
